/**
 * Logged-in Shop / Oblíbené machine — ACTIVE favorite sales-point contacts.
 * 0 → empty UX (guide to Organizace / Obchody) · 1 → hard-nav catalog on nested
 * `/account/shop/...` only (index `/account/shop` redirects to `/favorites`) · N → list.
 * On `/favorites` hub, autoSelect is suppressed (onFavoritesHub).
 * Star toggles favorite; unfavorite sets BE + guest opt-out so visit alone
 * will not re-add after logout (G7 cross-session).
 *
 * Display SoT: public Obchody directory allowlist (same as `/shops` + GuestFavorites).
 * Contacts API should already apply the Obchody predicate; FE intersects fail-closed
 * so undeployed BE / stale memberships never surface non-listable shops.
 *
 * Directory chrome (Shops parity): DirectoryPageHeader → List/Map → FindToolbar
 * (density in toolbar) → filtered list or ShopsDirectoryMap.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedName, normalizeLogoChipBackgroundSettings, normalizeLogoChipRimSettings } from 'pi-kiosk-shared';
import { useTheme } from '../../app/ThemeProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { ConfirmDialog } from '../../shared/ui/ConfirmDialog.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import {
  DirectoryEntityRow,
  type DirectoryEntityRowLayout,
} from '../../shared/ui/DirectoryEntityRow.js';
import { DirectoryFindToolbar } from '../../shared/ui/DirectoryFindToolbar.js';
import { DirectoryListMapViewSwitch } from '../../shared/ui/DirectoryListMapViewSwitch.js';
import { DirectoryPageHeader } from '../../shared/ui/DirectoryPageHeader.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import {
  DIRECTORY_DENSITY_STORAGE_KEYS,
  useDirectoryDensity,
  type DirectoryDensityMode,
} from '../../shared/ui/directoryDensity.js';
import {
  buildOrgFacets,
  filterDirectoryShops,
  type DirectorySortId,
} from '../../shared/ui/directoryListFilter.js';
import {
  readDirectoryGeoAllowedFlag,
  writeDirectoryGeoAllowedFlag,
} from '../../shared/ui/directoryGeoAllowed.js';
import {
  decideDirectoryGeoWarm,
  type DirectoryGeoPermissionProbe,
} from '../../shared/ui/directoryGeoWarmDecision.js';
import {
  DIRECTORY_CARD_ITEM_CLASS,
  DIRECTORY_CARD_PAD_CLASS,
  DIRECTORY_EMPTY_PANEL_MAX_CLASS,
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
  DIRECTORY_PAGE_STACK_NESTED_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { useShortLandscapeViewport } from '../../shared/hooks/useShortLandscapeViewport.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import { showToast } from '../../shared/ui/toastStore.js';
import {
  invalidateSalesPointContactsCache,
} from '../../shared/api/customerDirectoryCacheInvalidate.js';
import { toSameOriginCatalogImageUrl, classifyCatalogImageUrl } from 'pi-kiosk-shared';
import {
  PLATFORM_DIRECTORY_SHOPS_TENANT,
  readDirectoryFavoritesPersist,
  writeDirectoryFavoritesPersist,
} from '../../shared/session/searchPersistStorage.js';
import { isAccountAccessUsable } from './accountApi.js';
import {
  DIRECTORY_UI_PREFS_HYDRATED_EVENT,
  putDirectoryFavoritesSortRemote,
} from './useDirectoryUiPreferenceSync.js';
import { CUSTOMER_CONTENT_FULL_CLASS } from '../../app/customerTopBarStyles.js';
import {
  filterContactsByTenantCode,
  useSalesPointContactsByToken,
} from './salesPointContactsApi.js';
import { applyAccountShopFavoriteToggle } from './accountShopFavoriteToggle.js';
import {
  shopsDirectoryQueryOptions,
  useShopsDirectoryRealtimeSync,
} from '../shops/hooks/useShopsDirectoryRealtimeSync.js';
import {
  filterContactsByListableDirectory,
  filterOpenableSalesPointContacts,
  isContactListableOnDirectory,
} from './customerRetailListable.js';
import {
  fetchPublicCustomerSalesPointListableAllowlist,
  fetchPublicCustomerSalesPoints,
} from '../shops/publicSalesPointsApi.js';
import { ShopsDirectoryMap } from '../shops/ShopsDirectoryMap.js';
import { useShopsDirectoryMapViews } from '../shops/useShopsDirectoryMapViews.js';
import { useDirectoryViewModeSearch } from '../shops/useDirectoryViewModeSearch.js';
import type { SalesPointContactItemDTO } from './salesPointContactsTypes.js';
import {
  PLATFORM_BROWSE_PATH,
  PLATFORM_FAVORITES_PATH,
  PLATFORM_ORGS_PATH,
  PLATFORM_SHOPS_PATH,
} from '../shop/resolveShopHref.js';
import { useCustomerFeatureFlags } from '../flags/useCustomerFeatureFlags.js';
import { isCustomerBrowseEnabled } from '../../config/customerFeatureFlags.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { resolveShopLocationLabelWithT } from '../shops/resolveShopLocationLabel.js';

const GEO_TIMEOUT_MS = 10_000;

const FAVORITES_DIRECTORY_SORT_OPTIONS = [
  'nameAsc',
  'nameDesc',
  'orgAsc',
  'distanceAsc',
] as const satisfies readonly DirectorySortId[];

interface UserPosition {
  readonly lat: number;
  readonly lng: number;
}

/** Contact row + optional public-directory coords for distanceAsc / map join. */
type FavoriteContactFilterRow = SalesPointContactItemDTO & {
  readonly lat?: number | null;
  readonly lng?: number | null;
};

function acquireBrowserPosition(
  onSuccess: (position: UserPosition) => void,
  onError: (denied: boolean) => void,
): () => void {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.geolocation?.getCurrentPosition !== 'function'
  ) {
    onError(false);
    return () => undefined;
  }
  let cancelled = false;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      if (cancelled) {
        return;
      }
      onSuccess({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    },
    (error) => {
      if (cancelled) {
        return;
      }
      onError(error.code === error.PERMISSION_DENIED);
    },
    { timeout: GEO_TIMEOUT_MS, enableHighAccuracy: false },
  );
  return () => {
    cancelled = true;
  };
}

function favoritesResultItemClass(density: DirectoryDensityMode): string {
  return density === 'cards'
    ? [DIRECTORY_CARD_ITEM_CLASS, DIRECTORY_CARD_PAD_CLASS].join(' ')
    : DIRECTORY_FAVORITES_LIST_ITEM_CLASS;
}

/**
 * G1 residual Nákupy crash: partial contact DTOs can carry nullish tenantCode.
 * Unconditional `.trim()` threw after nullish URL/tenantName hardening.
 */
function isUsableTenantCode(code: unknown): code is string {
  return typeof code === 'string' && code.trim().length > 0;
}

function hardNavToCatalog(url: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.location.assign(url);
}

type ListableDirectoryAllowlist = {
  readonly allowedIds: Set<number>;
  readonly allowedSlugKeys: Set<string>;
};

function resolveContactRowPressHref(item: SalesPointContactItemDTO): string | undefined {
  const shopUrl = typeof item.customerShopUrl === 'string' ? item.customerShopUrl : '';
  if (shopUrl.length === 0) {
    return undefined;
  }
  const action = resolveDirectoryShopOpenAction({
    customerShopUrl: shopUrl,
    targetTenantCode: item.tenantCode,
  });
  return action?.url;
}

function isContactRowPressEnabled(
  item: SalesPointContactItemDTO,
  allowlistReady: boolean,
  allowlistData: ListableDirectoryAllowlist | undefined,
): boolean {
  const shopUrl = typeof item.customerShopUrl === 'string' ? item.customerShopUrl : '';
  if (shopUrl.length === 0) {
    return false;
  }
  if (!allowlistReady || allowlistData === undefined) {
    return false;
  }
  if (
    !isContactListableOnDirectory(
      item,
      allowlistData.allowedIds,
      allowlistData.allowedSlugKeys,
    )
  ) {
    return false;
  }
  return isUsableTenantCode(item.tenantCode);
}

export interface AccountShopContactsMachineProps {
  /**
   * Client-filter contacts to this tenant.
   * Primary `account/shop` omits / null (unfiltered). Tenant-scoped subviews (e.g. sales-points) may pass URL tenantCode.
   */
  readonly filterTenantCode?: string | null;
}

export function AccountShopContactsMachine({
  filterTenantCode = null,
}: AccountShopContactsMachineProps): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { pathname } = useLocation();
  const urlTenantCode = useTenantCode();
  const onFavoritesHub =
    pathname === PLATFORM_FAVORITES_PATH ||
    pathname.startsWith(`${PLATFORM_FAVORITES_PATH}/`);
  const contactsQuery = useSalesPointContactsByToken();
  const autoNavDoneRef = useRef(false);
  const queryClient = useQueryClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const {
    density,
    preference,
    setDensity,
    showToggle: showDensityToggle,
  } = useDirectoryDensity(DIRECTORY_DENSITY_STORAGE_KEYS.favorites);
  // G13 / G7 — directory SSE + consume isConnected for 30s poll when SSE down.
  const directorySseConnected = useShopsDirectoryRealtimeSync();
  const shortLandscape = useShortLandscapeViewport();
  const [favoritePendingId, setFavoritePendingId] = useState<number | null>(null);
  const [unfavoriteTarget, setUnfavoriteTarget] = useState<SalesPointContactItemDTO | null>(
    null,
  );
  const { flags } = useCustomerFeatureFlags();
  const showBrowseNav = isCustomerBrowseEnabled(flags);

  const scopedTenant =
    typeof filterTenantCode === 'string' && filterTenantCode.trim().length > 0
      ? filterTenantCode.trim()
      : null;

  const [query, setQuery] = useState(() => readDirectoryFavoritesPersist()?.q ?? '');
  const [sort, setSort] = useState<DirectorySortId>(
    () => readDirectoryFavoritesPersist()?.sort ?? 'nameAsc',
  );
  const [orgCode, setOrgCode] = useState<string | null>(
    () =>
      scopedTenant !== null ? null : (readDirectoryFavoritesPersist()?.orgCode ?? null),
  );
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  const [geoPending, setGeoPending] = useState(
    () => (readDirectoryFavoritesPersist()?.sort ?? 'nameAsc') === 'distanceAsc',
  );
  const { viewMode, setViewMode } = useDirectoryViewModeSearch();
  const [selectedSalesPointId, setSelectedSalesPointId] = useState<number | null>(null);
  const {
    savedViews,
    savedViewsLoading,
    savedViewsError,
    onRetrySavedViews,
    deletePending,
    renamePending,
    onRequestDeleteView,
    onRequestRenameView,
  } = useShopsDirectoryMapViews();

  useEffect(() => {
    writeDirectoryFavoritesPersist(
      {
        q: query,
        sort,
        orgCode: scopedTenant !== null ? null : orgCode,
      },
      {
        hasUserPosition: userPosition !== null,
        geoPending: sort === 'distanceAsc' && geoPending,
      },
    );
  }, [geoPending, orgCode, query, scopedTenant, sort, userPosition]);

  useEffect(() => {
    const onHydrated = (): void => {
      const restored = readDirectoryFavoritesPersist();
      const restoredSort = restored?.sort ?? 'nameAsc';
      setSort(restoredSort);
      setQuery(restored?.q ?? '');
      setOrgCode(
        scopedTenant !== null ? null : (restored?.orgCode ?? null),
      );
      setGeoPending(restoredSort === 'distanceAsc');
    };
    window.addEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    return () => {
      window.removeEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    };
  }, [scopedTenant]);

  useEffect(() => {
    if (sort !== 'distanceAsc' || userPosition !== null) {
      return;
    }
    let sortResetHandle: number | undefined;
    const release = acquireBrowserPosition(
      (position) => {
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
        setGeoPending(false);
      },
      (denied) => {
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        showToast({
          message: t(
            denied
              ? 'directory.filters.locationDenied'
              : 'directory.filters.locationUnavailable',
          ),
          variant: 'warn',
        });
        setUserPosition(null);
        setGeoPending(false);
        // Sync durable demotion before any timeout — unmount clearTimeout must not
        // leave DB/localStorage at distanceAsc (G3).
        writeDirectoryFavoritesPersist(
          {
            q: query,
            sort: 'nameAsc',
            orgCode: scopedTenant !== null ? null : orgCode,
          },
          { hasUserPosition: false, geoPending: false },
        );
        if (isAccountAccessUsable(session)) {
          putDirectoryFavoritesSortRemote(apiBaseUrl, 'nameAsc');
        }
        // Defer setSort only — avoids react-hooks/set-state-in-effect sync cascade.
        sortResetHandle = window.setTimeout(() => {
          setSort('nameAsc');
        }, 0);
      },
    );
    return () => {
      release();
      if (sortResetHandle !== undefined) {
        window.clearTimeout(sortResetHandle);
      }
    };
  }, [apiBaseUrl, orgCode, query, scopedTenant, session, sort, t, userPosition]);

  useEffect(() => {
    if (userPosition !== null) {
      return;
    }
    if (typeof navigator === 'undefined') {
      return;
    }

    let cancelled = false;
    let releaseAcquire: (() => void) | undefined;

    const warmAcquire = (): void => {
      if (cancelled || releaseAcquire !== undefined) {
        return;
      }
      releaseAcquire = acquireBrowserPosition(
        (position) => {
          if (cancelled) {
            return;
          }
          writeDirectoryGeoAllowedFlag(true);
          setUserPosition(position);
          setGeoPending(false);
        },
        (denied) => {
          if (denied) {
            writeDirectoryGeoAllowedFlag(false);
          }
        },
      );
    };

    const applyWarmDecision = (permission: DirectoryGeoPermissionProbe): void => {
      if (cancelled) {
        return;
      }
      const action = decideDirectoryGeoWarm(permission, readDirectoryGeoAllowedFlag());
      if (action === 'warm') {
        warmAcquire();
        return;
      }
      if (action === 'clearFlagAndSkip') {
        writeDirectoryGeoAllowedFlag(false);
      }
    };

    const permissionsQuery = navigator.permissions?.query;
    if (typeof permissionsQuery === 'function') {
      void permissionsQuery
        .call(navigator.permissions, { name: 'geolocation' })
        .then((status) => {
          if (
            status.state === 'granted' ||
            status.state === 'prompt' ||
            status.state === 'denied'
          ) {
            applyWarmDecision(status.state);
            return;
          }
          applyWarmDecision('unavailable');
        })
        .catch(() => {
          applyWarmDecision('queryFailed');
        });
    } else {
      applyWarmDecision('unavailable');
    }

    return () => {
      cancelled = true;
      releaseAcquire?.();
    };
  }, [userPosition]);

  const openableContacts = useMemo(() => {
    const all = filterOpenableSalesPointContacts(contactsQuery.data?.items ?? []);
    if (filterTenantCode !== null && filterTenantCode !== undefined) {
      return filterContactsByTenantCode(all, filterTenantCode);
    }
    return all;
  }, [contactsQuery.data?.items, filterTenantCode]);

  // Same unscoped Obchody directory as `/shops` — never contacts fail-open.
  // G7: shared key under public sales-points prefix (invalidates with `/shops`);
  // catalog-parity staleTime — keep last allowlist visible while background refetching
  // (blanking on every remount caused a double skeleton flash on Favorites).
  // Fail closed only until the first successful allowlist; refetchInterval mirrors `/shops`.
  const listableDirectoryQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPointListableAllowlist(apiBaseUrl),
    queryFn: async ({ signal }) => {
      const allowlist = await fetchPublicCustomerSalesPointListableAllowlist(apiBaseUrl, {
        signal,
      });
      return {
        allowedIds: new Set(allowlist.salesPointIds),
        allowedSlugKeys: new Set(allowlist.tenantSlugKeys),
      };
    },
    enabled: contactsQuery.isSuccess && openableContacts.length > 0,
    ...shopsDirectoryQueryOptions(directorySseConnected),
  });

  const allowlistReadyForDisplay =
    listableDirectoryQuery.isSuccess && listableDirectoryQuery.data !== undefined;

  const items = useMemo(() => {
    // Fail closed: enterable rows only after a successful public directory allowlist.
    if (!allowlistReadyForDisplay || listableDirectoryQuery.data === undefined) {
      return [];
    }
    return filterContactsByListableDirectory(
      openableContacts,
      listableDirectoryQuery.data.allowedIds,
      listableDirectoryQuery.data.allowedSlugKeys,
    );
  }, [allowlistReadyForDisplay, listableDirectoryQuery.data, openableContacts]);

  const favoriteSalesPointIds = useMemo(
    () => new Set(items.map((item) => item.salesPointId)),
    [items],
  );

  const needPublicDirectoryPages =
    items.length > 0 && (viewMode === 'map' || sort === 'distanceAsc');

  const publicDirectoryQuery = useInfiniteQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPoints(apiBaseUrl, null, null),
    queryFn: ({ pageParam, signal }) =>
      fetchPublicCustomerSalesPoints(apiBaseUrl, {
        cursor: pageParam,
        tenantCode: null,
        sort: null,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: needPublicDirectoryPages,
    ...shopsDirectoryQueryOptions(directorySseConnected),
  });

  const hasNextPage = publicDirectoryQuery.hasNextPage;
  const isFetchingNextPage = publicDirectoryQuery.isFetchingNextPage;
  const fetchNextPage = publicDirectoryQuery.fetchNextPage;

  useEffect(() => {
    if (!needPublicDirectoryPages) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [needPublicDirectoryPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const publicFavoritePages = useMemo(() => {
    const pages = publicDirectoryQuery.data?.pages ?? [];
    return pages.flatMap((page) =>
      page.items.filter((sp) => favoriteSalesPointIds.has(sp.salesPointId)),
    );
  }, [favoriteSalesPointIds, publicDirectoryQuery.data?.pages]);

  const coordsBySalesPointId = useMemo(() => {
    const map = new Map<number, { lat: number | null; lng: number | null }>();
    for (const sp of publicFavoritePages) {
      map.set(sp.salesPointId, { lat: sp.lat, lng: sp.lng });
    }
    return map;
  }, [publicFavoritePages]);

  const filterRows = useMemo((): FavoriteContactFilterRow[] => {
    return items.map((item) => {
      const coords = coordsBySalesPointId.get(item.salesPointId);
      const displayName = resolveLocalizedName(item.name, item.nameLocales, i18n.language);
      return {
        ...item,
        name: displayName,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
    });
  }, [coordsBySalesPointId, i18n.language, items]);

  const orgFacets = useMemo(
    () => (scopedTenant !== null ? [] : buildOrgFacets(filterRows)),
    [filterRows, scopedTenant],
  );

  const visibleItems = useMemo(
    () =>
      filterDirectoryShops(filterRows, {
        query,
        sort,
        orgCode: scopedTenant !== null ? null : orgCode,
        userLat: userPosition?.lat,
        userLng: userPosition?.lng,
      }),
    [filterRows, orgCode, query, scopedTenant, sort, userPosition],
  );

  const mapVisibleItems = useMemo(() => {
    if (viewMode !== 'map') {
      return [];
    }
    return filterDirectoryShops(publicFavoritePages, {
      query,
      sort,
      orgCode: scopedTenant !== null ? null : orgCode,
      userLat: userPosition?.lat,
      userLng: userPosition?.lng,
    });
  }, [
    orgCode,
    publicFavoritePages,
    query,
    scopedTenant,
    sort,
    userPosition,
    viewMode,
  ]);

  const [selectionFilterKey, setSelectionFilterKey] = useState(
    () => `${query}\0${orgCode ?? ''}\0${sort}`,
  );
  const nextSelectionFilterKey = `${query}\0${orgCode ?? ''}\0${sort}`;
  if (selectionFilterKey !== nextSelectionFilterKey) {
    setSelectionFilterKey(nextSelectionFilterKey);
    setSelectedSalesPointId(null);
  }

  const distanceSortActive = sort === 'distanceAsc' && userPosition !== null;
  const publicPagesDrained =
    !needPublicDirectoryPages ||
    (hasNextPage !== true && isFetchingNextPage !== true && !publicDirectoryQuery.isPending);
  const distanceListReady = distanceSortActive && publicPagesDrained;
  const mapReady =
    viewMode === 'map' &&
    !publicDirectoryQuery.isPending &&
    hasNextPage !== true &&
    isFetchingNextPage !== true;

  const filtersActive =
    query.trim().length > 0 ||
    (scopedTenant === null && orgCode !== null) ||
    sort !== 'nameAsc';

  const handleSortChange = (next: DirectorySortId): void => {
    if (next === 'distanceAsc') {
      if (userPosition === null) {
        setGeoPending(true);
      }
    } else {
      setGeoPending(false);
    }
    setSort(next);
    if (isAccountAccessUsable(session)) {
      putDirectoryFavoritesSortRemote(apiBaseUrl, next);
    }
  };

  const handleRequestMyLocation = (): void => {
    acquireBrowserPosition(
      (position) => {
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
      },
      (denied) => {
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        showToast({
          message: t(
            denied
              ? 'directory.filters.locationDenied'
              : 'directory.filters.locationUnavailable',
          ),
          variant: 'warn',
        });
      },
    );
  };

  const reconcilingListable =
    contactsQuery.isSuccess &&
    openableContacts.length > 0 &&
    listableDirectoryQuery.isPending;

  const autoSelectBase =
    filterTenantCode === null || filterTenantCode === undefined
      ? contactsQuery.data?.autoSelect === true && items.length === 1
      : items.length === 1;
  // Do not auto-nav (or stick on loading) when the sole row has a nullish tenantCode.
  const autoSelect =
    !onFavoritesHub &&
    autoSelectBase &&
    items[0] !== undefined &&
    isUsableTenantCode(items[0].tenantCode);

  const toggleFavorite = async (
    item: SalesPointContactItemDTO,
    favorite: boolean,
  ): Promise<void> => {
    await applyAccountShopFavoriteToggle({
      apiBaseUrl,
      item,
      favorite,
      session,
      queryClient,
      t,
      onPendingId: setFavoritePendingId,
      onClearUnfavoriteTarget: () => {
        setUnfavoriteTarget(null);
      },
    });
  };

  useEffect(() => {
    if (autoNavDoneRef.current) {
      return;
    }
    if (
      !contactsQuery.isSuccess ||
      !allowlistReadyForDisplay ||
      !autoSelect ||
      items.length !== 1
    ) {
      return;
    }
    const only = items[0];
    const shopUrl =
      only !== undefined && typeof only.customerShopUrl === 'string'
        ? only.customerShopUrl
        : '';
    if (only === undefined || shopUrl.length === 0) {
      return;
    }
    if (!isUsableTenantCode(only.tenantCode)) {
      return;
    }
    autoNavDoneRef.current = true;
    hardNavToCatalog(shopUrl);
  }, [
    allowlistReadyForDisplay,
    autoSelect,
    contactsQuery.isSuccess,
    items,
    urlTenantCode,
  ]);

  const title = t('account.shop.favoritesTitle');
  const description = t('account.shop.favoritesDescription');

  const pageHeader = (
    <DirectoryPageHeader
      title={title}
      description={description}
      titleAs="h1"
      testId="customer-favorites-header"
    />
  );

  const unfavoriteDialog = (
    <ConfirmDialog
      open={unfavoriteTarget !== null}
      title={t('account.shop.unfavoriteConfirmTitle')}
      description={t('account.shop.unfavoriteConfirmBody', {
        name: unfavoriteTarget
          ? resolveLocalizedName(
              unfavoriteTarget.name,
              unfavoriteTarget.nameLocales,
              i18n.language,
            )
          : '',
      })}
      confirmLabel={t('account.shop.unfavorite')}
      cancelLabel={t('shared.cancel')}
      confirmIntent="danger"
      pending={
        unfavoriteTarget !== null && favoritePendingId === unfavoriteTarget.salesPointId
      }
      testId="customer-favorites-unfavorite-confirm"
      onCancel={() => {
        if (favoritePendingId === null) {
          setUnfavoriteTarget(null);
        }
      }}
      onConfirm={() => {
        if (unfavoriteTarget !== null) {
          void toggleFavorite(unfavoriteTarget, false);
        }
      }}
    />
  );

  if (contactsQuery.isLoading) {
    return (
      <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS}>
        {pageHeader}
        <DirectoryResultsSkeleton
          density={density}
          listLayout="split2"
          size="lg"
          rowDensity="comfortable"
          testId="customer-favorites-skeleton-group"
        />
      </div>
    );
  }

  if (contactsQuery.isError) {
    return (
      <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS}>
        {pageHeader}
        <Card padded>
          <p className="text-sm text-[var(--color-danger)]">{t('account.shop.salesPointsError')}</p>
          <p className="mt-2 text-xs text-[var(--color-on-surface-muted)]">
            <Link className="text-[var(--color-accent)] underline" to={PLATFORM_SHOPS_PATH}>
              {t('account.shop.browsePublicShops')}
            </Link>
          </p>
        </Card>
      </div>
    );
  }

  if (reconcilingListable) {
    return (
      <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS} data-testid="customer-favorites-reconciling">
        {pageHeader}
        <DirectoryResultsSkeleton
          density={density}
          listLayout="split2"
          size="lg"
          rowDensity="comfortable"
          testId="customer-favorites-skeleton-group"
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS} data-testid="customer-favorites-empty">
        {pageHeader}
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('account.shop.favoritesEmpty')}
          </p>
          <p className="mt-3 text-sm text-[var(--color-on-surface)]">
            {t('account.shop.favoritesActivateHint')}
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            {showBrowseNav ? (
              <Link
                className="text-[var(--color-accent)] underline"
                to={PLATFORM_BROWSE_PATH}
                data-testid="customer-favorites-go-browse"
              >
                {t('nav.browse')}
              </Link>
            ) : null}
            <Link
              className="text-[var(--color-accent)] underline"
              to={PLATFORM_SHOPS_PATH}
              data-testid="customer-favorites-go-shops"
            >
              {t('nav.shops')}
            </Link>
            <Link
              className="text-[var(--color-accent)] underline"
              to={PLATFORM_ORGS_PATH}
              data-testid="customer-favorites-go-orgs"
            >
              {t('nav.orgs')}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (autoSelect) {
    return (
      <div className={DIRECTORY_PAGE_STACK_NESTED_CLASS}>
        {pageHeader}
        <Card padded>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('account.shop.loadingSalesPoints')}
          </p>
        </Card>
      </div>
    );
  }

  const mapLayoutActive =
    viewMode === 'map' && items.length > 0 && mapVisibleItems.length > 0;
  const compactMapChrome = mapLayoutActive && shortLandscape;

  return (
    <div
      className={[
        DIRECTORY_PAGE_STACK_NESTED_CLASS,
        mapLayoutActive ? 'min-h-0 flex-1' : '',
        // Cancel AccountShell py-4 on short landscape so map reclaim leftover height.
        compactMapChrome ? '-my-4 gap-2' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="customer-favorites-list"
      data-directory-map-layout={mapLayoutActive ? 'active' : undefined}
    >
      {compactMapChrome ? null : pageHeader}

      <DirectoryListMapViewSwitch
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        listLabel={t('directory.mapView.list')}
        mapLabel={t('directory.mapView.map')}
        groupAriaLabel={t('directory.mapView.viewSwitchAria')}
        listAriaLabel={t('directory.mapView.listAria')}
        mapAriaLabel={t('directory.mapView.mapAria')}
        testIdPrefix="customer-favorites"
      />

      {compactMapChrome ? null : (
        <div className="shrink-0">
          <DirectoryFindToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={handleSortChange}
            orgFacets={scopedTenant === null ? orgFacets : undefined}
            orgCode={orgCode}
            onOrgCodeChange={scopedTenant === null ? setOrgCode : undefined}
            sortOptions={FAVORITES_DIRECTORY_SORT_OPTIONS}
            searchPlaceholder={t('directory.filters.searchFavoritesShort')}
            testIdPrefix="customer-favorites-filters"
            densityMode={preference}
            onDensityChange={setDensity}
            showDensityToggle={showDensityToggle ? viewMode === 'list' : false}
            densityTestId="customer-favorites-density"
            toolbarTestId="customer-favorites-find-toolbar"
          />
        </div>
      )}

      {visibleItems.length === 0 &&
      !(distanceSortActive && !distanceListReady) &&
      viewMode === 'list' ? (
        <Card padded>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-favorites-no-matches"
          >
            {t('directory.filters.noMatches')}
          </p>
          {filtersActive ? (
            <Button
              type="button"
              intent="secondary"
              block
              className="mt-4"
              data-testid="customer-favorites-clear-filters"
              onClick={() => {
                setQuery('');
                setOrgCode(null);
                handleSortChange('nameAsc');
              }}
            >
              {t('directory.filters.clearAll')}
            </Button>
          ) : null}
        </Card>
      ) : null}

      {distanceSortActive && !distanceListReady && viewMode === 'list' ? (
        <p
          className="text-center text-sm text-[var(--color-on-surface-muted)]"
          data-testid="customer-favorites-distance-loading"
        >
          {t('account.shop.loadingSalesPoints')}
        </p>
      ) : null}

      {viewMode === 'map' && items.length > 0 && !mapReady ? (
        <p
          className="text-center text-sm text-[var(--color-on-surface-muted)]"
          data-testid="customer-favorites-map-loading"
        >
          {t('account.shop.loadingSalesPoints')}
        </p>
      ) : null}

      {viewMode === 'map' && items.length > 0 && mapVisibleItems.length > 0 ? (
        <div
          className={`${CUSTOMER_CONTENT_FULL_CLASS} flex min-h-0 min-w-0 flex-1 flex-col`}
        >
          <ShopsDirectoryMap
            visibleItems={mapVisibleItems}
            userPosition={userPosition}
            selectedSalesPointId={selectedSalesPointId}
            mapReady={mapReady}
            onSelectSalesPointId={setSelectedSalesPointId}
            onRequestMyLocation={handleRequestMyLocation}
            persistTenant={PLATFORM_DIRECTORY_SHOPS_TENANT}
            hideTenantName={scopedTenant !== null}
            showTenantChipOnSelectionCard={false}
            onBackToList={() => {
              setViewMode('list');
            }}
            savedViews={savedViews}
            savedViewsLoading={savedViewsLoading}
            savedViewsError={savedViewsError}
            onRetrySavedViews={onRetrySavedViews}
            onRequestDeleteView={onRequestDeleteView}
            onRequestRenameView={onRequestRenameView}
            deletePending={deletePending}
            renamePending={renamePending}
          />
        </div>
      ) : null}

      {viewMode === 'map' &&
      items.length > 0 &&
      mapVisibleItems.length === 0 &&
      mapReady ? (
        <Card padded>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-favorites-map-no-matches"
          >
            {t('directory.filters.noMatches')}
          </p>
          {filtersActive ? (
            <Button
              type="button"
              intent="secondary"
              block
              className="mt-4"
              data-testid="customer-favorites-clear-filters"
              onClick={() => {
                setQuery('');
                setOrgCode(null);
                handleSortChange('nameAsc');
              }}
            >
              {t('directory.filters.clearAll')}
            </Button>
          ) : null}
        </Card>
      ) : null}

      {viewMode === 'list' &&
      visibleItems.length > 0 &&
      !(distanceSortActive && !distanceListReady) ? (
        <DirectoryResults
          density={density}
          listLayout="split2"
          cardsLayout="dense"
          testId="customer-favorites-group"
        >
          {visibleItems.map((row) => {
            const item =
              items.find((candidate) => candidate.salesPointId === row.salesPointId) ??
              row;
            return (
              <li
                key={`${item.tenantCode}-${item.salesPointId}`}
                className={favoritesResultItemClass(density)}
                {...(density === 'cards' ? { 'data-density': 'cards' as const } : {})}
              >
                <ContactCard
                  item={item}
                  layout={density === 'cards' ? 'stack' : 'inline'}
                  disabled={favoritePendingId === item.salesPointId}
                  allowlistReady={allowlistReadyForDisplay}
                  allowlistData={listableDirectoryQuery.data}
                  onRequestUnfavorite={() => {
                    setUnfavoriteTarget(item);
                  }}
                  customerKey={session?.customerId ?? 'bearer'}
                />
              </li>
            );
          })}
        </DirectoryResults>
      ) : null}

      {unfavoriteDialog}
    </div>
  );
}

function ContactCard({
  item,
  disabled,
  allowlistReady,
  allowlistData,
  onRequestUnfavorite,
  customerKey,
  layout = 'inline',
}: {
  readonly item: SalesPointContactItemDTO;
  readonly disabled: boolean;
  readonly allowlistReady: boolean;
  readonly allowlistData: ListableDirectoryAllowlist | undefined;
  readonly onRequestUnfavorite: () => void;
  readonly customerKey: number | string;
  readonly layout?: DirectoryEntityRowLayout;
}): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { effectiveTheme } = useTheme();
  const displayName = resolveLocalizedName(item.name, item.nameLocales, i18n.language);
  const queryClient = useQueryClient();
  const imageUrl = toSameOriginCatalogImageUrl(item.imageUrl, {
    tenantCode: item.tenantCode,
  });
  const imageUrlDark = toSameOriginCatalogImageUrl(item.imageUrlDark, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrl = toSameOriginCatalogImageUrl(item.tenantLogoUrl, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrlDark = toSameOriginCatalogImageUrl(item.tenantLogoUrlDark, {
    tenantCode: item.tenantCode,
  });

  const onMediaFailed = useCallback(
    (failedSrc: string): void => {
      if (classifyCatalogImageUrl(failedSrc) === 'signed_api') {
        invalidateSalesPointContactsCache(queryClient, customerKey);
      }
    },
    [customerKey, queryClient],
  );

  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl,
    imageUrlDark,
    tenantLogoUrl,
    tenantLogoUrlDark,
    monogram: {
      id: item.salesPointId,
      displayName,
      code: item.code,
    },
    showTenantChip: false,
    size: 'lg',
    loading: 'lazy',
    logoChipRim: normalizeLogoChipRimSettings(item),
    logoChipBackground: normalizeLogoChipBackgroundSettings(item),
    logoChipRimTheme: effectiveTheme,
    onMediaFailed,
    testId: `customer-favorites-thumb-${item.salesPointId}`,
  });

  const tenantLabel = typeof item.tenantName === 'string' ? item.tenantName.trim() : '';
  // A13b — ONLINE_ONLY → Shipping only (never locationMissing for null ops address).
  const locationLabel = resolveShopLocationLabelWithT(
    {
      customerPresence: item.customerPresence,
      location: item.location,
      locationMissingKey: 'account.shop.locationMissing',
    },
    t,
  );
  const subtitle =
    (tenantLabel.length > 0 ? `${tenantLabel} · ` : '') + locationLabel;

  const rowPressHref = resolveContactRowPressHref(item);
  const rowPressEnabled = isContactRowPressEnabled(item, allowlistReady, allowlistData);

  return (
    <DirectoryEntityRow
      thumb={thumbProps}
      title={displayName}
      subtitle={subtitle}
      rowPressHref={rowPressHref}
      rowPressDisabled={disabled || !rowPressEnabled || rowPressHref === undefined}
      rowPressTestId={`customer-favorites-open-title-${item.salesPointId}`}
      showChevron
      density="comfortable"
      layout={layout}
      testId={`customer-favorites-row-${item.salesPointId}`}
      trailing={
        <button
          type="button"
          className={[
            'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent',
            'text-[var(--color-warning)] hover:bg-[var(--color-surface-muted)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            'disabled:cursor-not-allowed disabled:opacity-[0.55]',
          ].join(' ')}
          data-testid={`customer-favorites-unstar-${item.salesPointId}`}
          title={t('account.shop.unfavorite')}
          aria-label={t('account.shop.unfavorite')}
          disabled={disabled}
          onClick={onRequestUnfavorite}
        >
          <FavoriteStarIcon filled />
        </button>
      }
    />
  );
}
